import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import {
  APPLY_CODES_FLOW_SELECTION,
  REQUEST_CODES_FLOW,
  UPLOAD_CODES_FLOW,
  AUTHORIZE_CODES_FLOW,
  AUTHORIZATION_SENT,
  APPLY_CODES_VIEW,
  UPLOAD_ART_VIEW,
  CUSTOMIZE_VIEW,
  LOGIN_SIGNUP_VIEW,
} from '@app/constants';
import { navigateToView } from '@app/router';
import { loadProject, isValidArtwork } from '@app/utils/utils';
import { createContext, processError } from '@app/actions/artworkDataActions';
import { resetCustomizeData } from '@app/actions/customizeActions'
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import { fetchBrandIdList, setApplyCodesStep } from '@app/actions/applyCodesActions';
import { getAuthorizationStatus } from '@app/actions/authActions';

import ApplyCodesFlowSelection from './apply-codes-flow-selection/ApplyCodesFlowSelection';
import RequestCodes from './request-codes/RequestCodes';
import UploadCodes from './upload-codes/UploadCodes';
import AuthorizeCodes from './authorize-codes/AuthorizeCodes';
import AuthorizeCodesFinish from './authorize-codes/authorize-codes-finish/AuthorizeCodesFinish';
import './ApplyCodesView.scss';

const ApplyCodesView = ({
  flow,
  matrix,
  context,
  endLoading,
  flowConfig,
  brandIdList,
  artworkInfo,
  isAuthorized,
  startLoading,
  processError,
  updateContext,
  createContext,
  fetchBrandIdList,
  applyCodesStep,
  setApplyCodesStep,
  resetCustomizeData,
  getAuthorizationStatus,
}) => {
  const selectApplyCodesStep = useCallback(applyCodesStep => setApplyCodesStep(applyCodesStep), [setApplyCodesStep]);

  const navigateToCustomize = () => {
    flow.editor = null;
    context.project = null;
    resetCustomizeData();

    const {
      projectData: {
        product: { id: productId },
        projectLocation,
      }
    } = flow;

    if (isValidArtwork(artworkInfo, flowConfig)) {
      navigateToView(CUSTOMIZE_VIEW, projectLocation, productId);
    } else {
      navigateToView(UPLOAD_ART_VIEW, projectLocation, productId, { designName: '' });
    }
  };

  const onProjectRestored = useCallback((sender, { projectLocation }) => navigateToView(APPLY_CODES_VIEW, projectLocation), []);
  const onProjectCreated = useCallback(
    (sender, projectData) => {
      const {
        product: { id: productId },
        projectLocation,
      } = projectData;
      navigateToView(APPLY_CODES_VIEW, projectLocation, productId);
      flow.loadProject(projectData);
    },
    [flow],
  );
  const onProjectLoaded = useCallback(
    (sender, project) => {
      createContext(flow, context, project).then(newContext => {
        updateContext(newContext);
      });
    },
    [flow, context, createContext, updateContext],
  );
  const processProjectLoad = useCallback(() => {
    loadProject(artworkInfo, flow, context, matrix);
  }, [artworkInfo, context, flow, matrix]);

  const subscribeToFlowEvents = useCallback(() => {
    flow.on('loading:started', () => startLoading(), this);
    flow.on('loading:finished', () => endLoading(), this);
    flow.on('project:loaded', onProjectLoaded, this);
    flow.on('project:created', onProjectCreated, this);
    flow.on('project:restored', onProjectRestored, this);
    flow.on('error:occurred', processError, this);
  }, [flow, onProjectLoaded, onProjectCreated, onProjectRestored, startLoading, endLoading, processError]);
  const unsubscribeFromFlowEvents = useCallback(() => {
    flow.un('loading:started', () => startLoading(), this);
    flow.un('loading:finished', () => endLoading(), this);
    flow.un('project:loaded', onProjectLoaded, this);
    flow.un('project:created', onProjectCreated, this);
    flow.un('project:restored', onProjectRestored, this);
    flow.un('error:occurred', processError, this);
  }, [flow, onProjectLoaded, onProjectCreated, onProjectRestored, startLoading, endLoading, processError]);

  useEffect(() => {
    !brandIdList && isAuthorized && fetchBrandIdList(flowConfig);
  }, [fetchBrandIdList, brandIdList, flowConfig, isAuthorized]);

  useEffect(() => {
    getAuthorizationStatus();
  }, [getAuthorizationStatus]);

  useEffect(() => {
    const prevView = sessionStorage.getItem('prevView');
    if (prevView === LOGIN_SIGNUP_VIEW) {
      isAuthorized ? selectApplyCodesStep(REQUEST_CODES_FLOW) : selectApplyCodesStep(APPLY_CODES_FLOW_SELECTION);
    }
    sessionStorage.setItem('prevView', '');
  }, [isAuthorized, selectApplyCodesStep]);

  useEffect(() => {
    subscribeToFlowEvents();
    return unsubscribeFromFlowEvents;
  }, [subscribeToFlowEvents, unsubscribeFromFlowEvents]);

  useEffect(processProjectLoad, []);

  const applyCodesStepLayout = {
    [APPLY_CODES_FLOW_SELECTION]: <ApplyCodesFlowSelection {...{ selectApplyCodesStep, navigateToCustomize }} />,
    [REQUEST_CODES_FLOW]: <RequestCodes {...{ selectApplyCodesStep }} />,
    [UPLOAD_CODES_FLOW]: <UploadCodes {...{ selectApplyCodesStep }} />,
    [AUTHORIZE_CODES_FLOW]: <AuthorizeCodes {...{ selectApplyCodesStep }} />,
    [AUTHORIZATION_SENT]: <AuthorizeCodesFinish />,
  };

  return <div id="qau-apply-codes-view">{applyCodesStepLayout[applyCodesStep]}</div>;
};

const mapStateToProps = ({
  applyCodes: { brandIdList, applyCodesStep },
  artwork: { info: artworkInfo },
  auth: { isAuthorized },
  matrix,
}) => ({
  brandIdList,
  isAuthorized,
  applyCodesStep,
  artworkInfo,
  matrix,
});

const mapDispatchToProps = {
  fetchBrandIdList,
  getAuthorizationStatus,
  resetCustomizeData,
  setApplyCodesStep,
  createContext,
  startLoading,
  endLoading,
  processError,
};

ApplyCodesView.propTypes = {
  matrix: PropTypes.object,
  brandIdList: PropTypes.array,
  artworkInfo: PropTypes.object,
  flow: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  endLoading: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  flowConfig: PropTypes.object.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
  processError: PropTypes.func.isRequired,
  createContext: PropTypes.func.isRequired,
  updateContext: PropTypes.func.isRequired,
  fetchBrandIdList: PropTypes.func.isRequired,
  applyCodesStep: PropTypes.string.isRequired,
  setApplyCodesStep: PropTypes.func.isRequired,
  getAuthorizationStatus: PropTypes.func.isRequired,
  resetCustomizeData: PropTypes.func.isRequired,
};

export default inject('flow', 'context', 'flowConfig', 'updateContext')(connect(mapStateToProps, mapDispatchToProps)(ApplyCodesView));
